import React, { useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { BdsButton, BdsChipTag, BdsDivider, BdsIcon } from '@/core/constants/webComponents';
import type { SelectionMap } from '../../contexts/MultipleEditionContext';
import type { Gender } from '../ClassificationCard/types';
import './FilteredConfirmationModal.scss';

const GENDER_LABEL: Record<Gender, string> = {
  man: 'Homem',
  woman: 'Mulher',
  company: 'Marca',
};

interface FilteredConfirmationModalProps {
  isOpen: boolean;
  totalPosts: number;
  groupsSelection: SelectionMap;
  themesSelection: SelectionMap;
  interestsSelection: SelectionMap;
  tagsSelection: SelectionMap;
  sentimentSelection: string | null;
  genderSelection: Gender | null;
  checkedActive: boolean;
  removeActive: boolean;
  allVisiblePostsAreSpam: boolean;
  onCancel: () => void;
  onConfirm: () => void;
}

const pick = (map: SelectionMap, state: 'add' | 'remove') =>
  Object.entries(map).filter(([, s]) => s === state).map(([n]) => n);

type ChipColor = 'success' | 'danger' | 'disabled' | 'outline' | 'default' | 'info' | 'warning';

interface SectionProps {
  title: string;
  items: Array<{ label: string; values?: string[]; inline?: boolean; chipColor?: ChipColor }>;
}

const ConfirmSection: React.FC<SectionProps> = ({ title, items }) => (
  <div className="filtered-confirm-modal__section">
    <p className="filtered-confirm-modal__section-title">{title}</p>
    {items.map(({ label, values, inline, chipColor }) => (
      <div key={label} className={`filtered-confirm-modal__item${inline ? ' filtered-confirm-modal__item--inline' : ''}`}>
        <span className="filtered-confirm-modal__item-label">{label}{inline ? ':' : ''}</span>
        {values && values.length > 0 && (
          <div className="filtered-confirm-modal__item-chips">
            {values.map((v) => (
              <BdsChipTag key={v} color={chipColor ?? 'outline'}>{v}</BdsChipTag>
            ))}
          </div>
        )}
      </div>
    ))}
  </div>
);

export const FilteredConfirmationModal: React.FC<FilteredConfirmationModalProps> = ({
  isOpen,
  totalPosts,
  groupsSelection,
  themesSelection,
  interestsSelection,
  tagsSelection,
  sentimentSelection,
  genderSelection,
  checkedActive,
  removeActive,
  allVisiblePostsAreSpam,
  onCancel,
  onConfirm,
}) => {
  const { t } = useTranslation();

  useEffect(() => {
    if (!isOpen) return;
    const handleKeyDown = (e: KeyboardEvent) => { if (e.key === 'Escape') onCancel(); };
    globalThis.addEventListener('keydown', handleKeyDown);
    return () => globalThis.removeEventListener('keydown', handleKeyDown);
  }, [isOpen, onCancel]);

  if (!isOpen) return null;

  const addItems: Array<{ label: string; values?: string[]; inline?: boolean; chipColor?: ChipColor }> = [];
  const removeItems: Array<{ label: string; values?: string[]; inline?: boolean; chipColor?: ChipColor }> = [];

  const addGroups = pick(groupsSelection, 'add');
  const rmGroups = pick(groupsSelection, 'remove');
  const addThemes = pick(themesSelection, 'add');
  const rmThemes = pick(themesSelection, 'remove');
  const addInterests = pick(interestsSelection, 'add');
  const rmInterests = pick(interestsSelection, 'remove');
  const addTags = pick(tagsSelection, 'add');
  const rmTags = pick(tagsSelection, 'remove');

  if (addGroups.length) addItems.push({ label: t('classificationCard.groups'), values: addGroups });
  if (addThemes.length) addItems.push({ label: t('classificationCard.themes'), values: addThemes });
  if (addInterests.length) addItems.push({ label: t('classificationCard.interests'), values: addInterests });
  if (addTags.length) addItems.push({ label: t('classificationCard.tags'), values: addTags });
  const SENTIMENT_CHIP_COLOR: Record<string, ChipColor> = {
    Positivo: 'success',
    Negativo: 'danger',
    Neutro: 'disabled',
  };

  if (sentimentSelection !== null) addItems.push({ label: t('multipleEdition.sentiment'), values: [sentimentSelection], inline: true, chipColor: SENTIMENT_CHIP_COLOR[sentimentSelection] ?? 'outline' });
  if (genderSelection !== null) addItems.push({ label: t('multipleEdition.profileType'), values: [GENDER_LABEL[genderSelection]], inline: true });
  if (checkedActive) addItems.push({ label: t('multipleEdition.checkedButton'), values: [t('common.yes')], inline: true });
  if (removeActive) addItems.push({ label: allVisiblePostsAreSpam ? t('multipleEdition.restoreButton') : t('multipleEdition.removeButton'), values: [t('common.yes')], inline: true, chipColor: 'success' });

  if (rmGroups.length) removeItems.push({ label: t('classificationCard.groups'), values: rmGroups });
  if (rmThemes.length) removeItems.push({ label: t('classificationCard.themes'), values: rmThemes });
  if (rmInterests.length) removeItems.push({ label: t('classificationCard.interests'), values: rmInterests });
  if (rmTags.length) removeItems.push({ label: t('classificationCard.tags'), values: rmTags });

  return (
    <dialog
      open
      className="filtered-confirm-modal"
      aria-modal="true"
      aria-labelledby="filtered-confirm-modal-title"
    >
      <div className="filtered-confirm-modal__header">
        <span className="filtered-confirm-modal__title" id="filtered-confirm-modal-title">
          {t('multipleEdition.confirmFilteredTitle')}
        </span>
        <button className="filtered-confirm-modal__close" onClick={onCancel} aria-label={t('common.close')}>
          <BdsIcon name="close" size="small" />
        </button>
      </div>
      <div className="filtered-confirm-modal__body">
        <p className="filtered-confirm-modal__body-summary">
          <span>{t('multipleEdition.confirmFilteredBodyPrefix')}</span>
          <span className="filtered-confirm-modal__body-bold">{t('multipleEdition.confirmFilteredBodyCount', { count: totalPosts })}</span>
        </p>
        <BdsDivider class="filtered-confirm-modal__divider" />
        {addItems.length > 0 && (
          <ConfirmSection title={t('multipleEdition.confirmFilteredClassifiedBy')} items={addItems} />
        )}
        {removeItems.length > 0 && (
          <>
            <BdsDivider
              class="filtered-confirm-modal__section-divider"
              style-type="solid"
              orientation="horizontal"
              color="divider-1"
            />
            <ConfirmSection title={t('multipleEdition.confirmFilteredRemovedBy')} items={removeItems} />
          </>
        )}
      </div>
      <div className="filtered-confirm-modal__footer">
        <BdsButton variant="outline" size="short" onClick={onCancel}>
          {t('multipleEdition.confirmCancel')}
        </BdsButton>
        <BdsButton variant="solid" size="short" color="primary" onClick={onConfirm}>
          {t('multipleEdition.confirmSave')}
        </BdsButton>
      </div>
    </dialog>
  );
};
